import * as React from 'react'
import { isBugsnapUiEvent } from '@features/bug-report/bugsnapUi'
import './dropdown-menu.css'

// Simple dropdown menu implementation
interface DropdownMenuProps {
  children: React.ReactNode
}

interface DropdownMenuTriggerProps {
  children: React.ReactNode
  asChild?: boolean
}

interface DropdownMenuContentProps {
  children: React.ReactNode
  align?: 'start' | 'center' | 'end'
  className?: string
}

interface DropdownMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  className?: string
  /** When true, item is non-interactive and does not close the menu on click */
  disabled?: boolean
}

interface DropdownMenuSeparatorProps {
  className?: string
}

const DropdownMenuContext = React.createContext<{
  open: boolean
  setOpen: (open: boolean) => void
}>({ open: false, setOpen: () => {} })

const DropdownMenu: React.FC<DropdownMenuProps> = ({ children }) => {
  const [open, setOpen] = React.useState(false)
  const value = React.useMemo(() => ({ open, setOpen }), [open])
  return (
    <DropdownMenuContext.Provider value={value}>
      <div className="dropdown-menu__root">{children}</div>
    </DropdownMenuContext.Provider>
  )
}

const DropdownMenuTrigger: React.FC<DropdownMenuTriggerProps> = ({ children, asChild }) => {
  const { setOpen } = React.useContext(DropdownMenuContext)

  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<{
      onClick?: React.MouseEventHandler<Element>
    }>
    return React.cloneElement(child, {
      onClick: (e: React.MouseEvent<Element>) => {
        child.props.onClick?.(e)
        setOpen((was) => !was)
      },
    })
  }

  return (
    <button type="button" onClick={() => setOpen((was) => !was)}>
      {children}
    </button>
  )
}

const DropdownMenuContent: React.FC<DropdownMenuContentProps> = ({
  children,
  align = 'end',
  className = '',
}) => {
  const { open, setOpen } = React.useContext(DropdownMenuContext)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return

    const handleMouseDown = (event: MouseEvent) => {
      // Stay open while the user files a Bugsnap about this UI (see bugsnapUi).
      if (isBugsnapUiEvent(event.target, ref.current)) return
      const menuEl = ref.current
      if (!menuEl) return

      const root = menuEl.closest('.dropdown-menu__root')
      const targetNode = event.target as Node | null

      if (menuEl.contains(targetNode)) return

      /* Clicks on the trigger live inside `.dropdown-menu__root` but outside the floating panel */
      if (root?.contains(targetNode)) return

      setOpen(false)
    }

    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open, setOpen])

  if (!open) return null

  let alignMod = 'dropdown-menu__content--align-center'
  if (align === 'start') {
    alignMod = 'dropdown-menu__content--align-start'
  } else if (align === 'end') {
    alignMod = 'dropdown-menu__content--align-end'
  }

  return (
    <div ref={ref} className={`dropdown-menu__content ${alignMod} ${className}`.trim()} role="menu">
      {children}
    </div>
  )
}

const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({
  children,
  className = '',
  disabled = false,
  onClick,
  ...props
}) => {
  const { setOpen } = React.useContext(DropdownMenuContext)
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return
    onClick?.(e)
    setOpen(false)
  }

  return (
    <div
      role="menuitem"
      tabIndex={-1}
      aria-disabled={disabled}
      className={`dropdown-menu__item ${disabled ? 'dropdown-menu__item--disabled' : ''} ${className}`.trim()}
      onClick={handleClick}
      {...props}
    >
      {children}
    </div>
  )
}

const DropdownMenuSeparator: React.FC<DropdownMenuSeparatorProps> = ({ className = '' }) => (
  <div className={`dropdown-menu__separator ${className}`.trim()} aria-hidden />
)

const DropdownMenuLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => <div className={`dropdown-menu__label ${className}`.trim()}>{children}</div>

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
}
